<template>
  <LandingSection
    id="advantages"
    :title="t('landing.advantages.title')"
    section-class="landing-section relative overflow-hidden py-24 lg:py-28"
    title-class="landing-heading landing-advantages-heading text-4xl"
    container-class="landing-advantages-shell container mx-auto px-4 sm:px-6"
  >
    <div class="landing-advantages-grid">
      <article
        v-for="advantage in advantages"
        :key="advantage.titleKey"
        class="landing-advantage-card landing-card"
      >
        <span class="landing-advantage-serial-ghost" aria-hidden="true">{{ advantage.serial }}</span>

        <div class="landing-advantage-top">
          <div class="landing-advantage-icon-wrap">
            <div v-html="advantage.icon" class="landing-advantage-icon"></div>
          </div>
          <p class="landing-advantage-serial">{{ advantage.serial }}</p>
        </div>

        <div class="landing-advantage-copy">
          <h3 class="landing-advantage-title landing-card-title">{{ advantage.title }}</h3>
          <p class="landing-advantage-description">{{ advantage.description }}</p>
        </div>

        <div class="landing-advantage-meter" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </article>
    </div>
  </LandingSection>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LandingSection from '@/components/landing/LandingSection.vue'
import { landingAdvantages as advantagesSource } from '@/core/data/landingContent'

const { t } = useI18n()

const normalizeIconColor = icon => icon
  .replaceAll('stroke="url(#theme-gradient)"', 'stroke="currentColor"')
  .replaceAll('fill="url(#theme-gradient)"', 'fill="currentColor"')
  .replaceAll('theme-gradient', 'landing-advantage-gradient')

const advantages = computed(() => advantagesSource.map((advantage, index) => ({
  ...advantage,
  serial: String(index + 1).padStart(2, '0'),
  title: t(advantage.titleKey),
  description: t(advantage.descriptionKey),
  icon: normalizeIconColor(advantage.icon).replaceAll(
    'landing-advantage-gradient',
    `landing-advantage-gradient-${index}`
  )
})))
</script>

<style scoped>
:deep(.landing-advantages-shell) {
  position: relative;
  z-index: 1;
}

:deep(.landing-advantages-heading) {
  margin-bottom: clamp(2.75rem, 6vw, 4.5rem);
  text-align: center;
}

.landing-advantage-icon {
  width: 2.6rem;
  height: 2.6rem;
  color: var(--text-primary);
}

.landing-advantage-icon :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.landing-advantages-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: clamp(1rem, 2.4vw, 1.75rem);
}

.landing-advantage-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(1.5rem, 2vw, 2.25rem);
  min-height: clamp(18rem, 30vw, 23rem);
  grid-column: span 6;
  overflow: hidden;
  isolation: isolate;
  background:
    linear-gradient(
      155deg,
      color-mix(in srgb, var(--surface-elevated) 92%, var(--theme-primary) 2%) 0%,
      color-mix(in srgb, var(--surface-card) 92%, var(--surface-page)) 100%
    );
}

.landing-advantage-card::before {
  content: '';
  position: absolute;
  pointer-events: none;
  top: -3.5rem;
  right: -2.75rem;
  width: 14rem;
  height: 14rem;
  border-radius: 999px;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--theme-gradient-end) 18%, transparent) 0%,
    transparent 70%
  );
  opacity: 0.9;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.landing-advantage-card:hover::before {
  transform: scale(1.08);
}

.landing-advantage-card:nth-child(1),
.landing-advantage-card:nth-child(4) {
  grid-column: span 7;
}

.landing-advantage-card:nth-child(2),
.landing-advantage-card:nth-child(3) {
  grid-column: span 5;
}

.landing-advantage-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.landing-advantage-icon-wrap {
  display: grid;
  place-items: center;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 1.35rem;
  background:
    linear-gradient(
      145deg,
      color-mix(in srgb, var(--theme-primary) 10%, var(--surface-card-hover)) 0%,
      color-mix(in srgb, var(--surface-card) 90%, var(--surface-page)) 100%
    );
  border: 1px solid color-mix(in srgb, var(--theme-primary) 18%, var(--border-default));
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--surface-card) 88%, transparent),
    0 12px 24px color-mix(in srgb, var(--shadow-color) 20%, transparent);
}

.landing-advantage-serial,
.landing-advantage-serial-ghost {
  font-family: 'Montserrat', sans-serif;
}

.landing-advantage-serial {
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.28em;
  color: color-mix(in srgb, var(--text-muted) 78%, var(--theme-primary));
}

.landing-advantage-serial-ghost {
  position: absolute;
  top: 1rem;
  right: 1.35rem;
  z-index: -1;
  font-size: clamp(4rem, 10vw, 7rem);
  font-weight: 700;
  line-height: 0.88;
  letter-spacing: -0.06em;
  color: color-mix(in srgb, var(--theme-primary) 7%, var(--surface-card));
}

.landing-advantage-copy {
  display: grid;
  gap: 0.9rem;
  max-width: 34rem;
}

.landing-advantage-description {
  margin: 0;
  max-width: 38ch;
  color: var(--text-secondary);
  font-size: 1.02rem;
  line-height: 1.72;
}

.landing-advantage-meter {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.landing-advantage-meter span {
  display: block;
  height: 0.38rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--theme-primary) 20%, var(--surface-card-hover));
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), background 0.35s ease;
  transform-origin: left center;
}

.landing-advantage-meter span:nth-child(1) {
  width: 3.2rem;
}

.landing-advantage-meter span:nth-child(2) {
  width: 1.8rem;
  background: color-mix(in srgb, var(--theme-accent) 24%, var(--surface-card-hover));
}

.landing-advantage-meter span:nth-child(3) {
  width: 4.6rem;
}

.landing-advantage-card:hover .landing-advantage-meter span {
  transform: scaleX(1.08);
}

@media (max-width: 1160px) {
  .landing-advantage-card,
  .landing-advantage-card:nth-child(1),
  .landing-advantage-card:nth-child(2),
  .landing-advantage-card:nth-child(3),
  .landing-advantage-card:nth-child(4) {
    grid-column: span 6;
    min-height: clamp(17rem, 32vw, 21rem);
    margin-top: 0;
  }
}

@media (max-width: 767px) {
  :deep(.landing-advantages-heading) {
    text-align: center;
  }

  .landing-advantage-card,
  .landing-advantage-card:nth-child(1),
  .landing-advantage-card:nth-child(2),
  .landing-advantage-card:nth-child(3),
  .landing-advantage-card:nth-child(4) {
    grid-column: span 12;
    min-height: auto;
  }

  .landing-advantage-icon-wrap {
    width: 4rem;
    height: 4rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing-advantage-card,
  .landing-advantage-card::before,
  .landing-advantage-meter span {
    transition: none;
  }
}
</style>
